import isEqual from 'lodash/isEqual'
import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import type { EditableTrack, WriterSplit } from 'types/track'

/**
 * Gets the currently edited track from the server state.
 * @param state - The Redux state
 * @returns The edited track object
 */
const getEditedTrack = (state: State): EditableTrack => state.server.editedTrack

/**
 * Gets the metadata of the currently edited track.
 * @param state - The Redux state
 * @returns The edited track's metadata
 */
const getEditedTrackMetadata = (state: State) => getEditedTrack(state).metadata

/**
 * Gets the original metadata of the edited track before changes.
 * @param state - The Redux state
 * @returns The original track metadata
 */
const getOriginalMetadata = (state: State) =>
  state.server.originalEditedTrackMetadata

/**
 * Checks if there are unsaved changes to the edited track.
 * @param state - The Redux state
 * @returns True if there are unsaved changes, false otherwise
 */
export const hasUnsavedChanges = createSelector(
  [getEditedTrackMetadata, getOriginalMetadata],
  (editedTrackMetadata, originalMetadata) => {
    return !isEqual(editedTrackMetadata, originalMetadata)
  },
)
/**
 * Gets the writer splits for the currently edited track.
 * @param state - The Redux state
 * @returns Array of writer split objects
 */
export const getEditedTrackWriterSplits = (state: State): WriterSplit[] =>
  getEditedTrack(state)?.track_writers ?? []
